import { Avatar, Box, Skeleton, useTheme } from '@mui/material';
import { Button } from '@portals/core/src/components/Button/Button';
import { Divider } from '@portals/core/src/components/Divider/Divider';
import { List, ListItem, ListItemAvatar, ListItemText } from '@portals/core/src/components/List/List';
import { Pill } from '@portals/core/src/components/Pill/Pill';
import { Spacer } from '@portals/core/src/components/Spacer/Spacer';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import type { Theme } from '@portals/core/src/themes/themes';
import { ArrowRight, Home } from '@portals/icons';
import React, { KeyboardEvent, MouseEventHandler, useCallback } from 'react';

import { LocationItem } from './LocationItem';
import { getStyles } from './LocationsList.styles';
import { type Location } from './LocationsMap';

interface Props {
  locations: Location[];
  loading?: boolean;

  activeId: number | null;
  clearActive: MouseEventHandler;
  onListItemClick: (location: Location) => void;

  hoverId: number | null;

  t?: (key: string, options?: Record<string, unknown>) => string;
}

const LocationItemSkeleton = () => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);

  return (
    <>
      <ListItem sx={styles.item}>
        <ListItemAvatar sx={styles.avatar}>
          <Spacer margin="large" />
          <Skeleton variant="circular" width={50} height={50} sx={{ marginLeft: 5 }} />
        </ListItemAvatar>
        <ListItemText>
          <Typography variant="h6" component="div">
            <Skeleton />
          </Typography>
          <Spacer />
          <Typography variant="h5" component="div">
            <Skeleton />
          </Typography>
          <Typography variant="body2" component="div">
            <Skeleton />
          </Typography>
          <Spacer />
          <Pill label={<Skeleton variant="text" sx={{ minWidth: 100 }} />} />
        </ListItemText>
        <ListItemAvatar>
          <Spacer margin="large" />
          <ArrowRight fontSize="small" />
        </ListItemAvatar>
      </ListItem>
      <Divider sx={styles.divider} />
    </>
  );
};

interface StatusIconProps {
  item: Location;
  index: number;
  isActive: boolean;
  isHover: boolean;
}

const StatusIcon: React.FC<StatusIconProps> = ({ isHover, index }) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);
  const sx = Object.assign({}, styles.avatar, isHover ? styles.avatarHover : {});

  return (
    <Box sx={sx} alignSelf="flex-start">
      {isHover ? (
        <Avatar
          sx={{
            width: { xs: '24px', md: '36px', lg: '50px' },
            height: { xs: '24px', md: '36px', lg: '50px' },
            backgroundColor: theme.palette.grey.A100,
          }}
        >
          <Avatar
            sx={{
              width: { xs: '22px', md: '34px', lg: '48px' },
              height: { xs: '22px', md: '34px', lg: '48px' },
              backgroundColor: theme.palette.grey.A100,
              color: theme.palette.secondary.contrastText,
              border: '1px solid',
              borderColor: theme.palette.secondary.contrastText,
            }}
          >
            {index}
          </Avatar>
        </Avatar>
      ) : null}
      {!isHover ? (
        <Avatar
          sx={{
            width: { xs: '24px', md: '36px', lg: '50px' },
            height: { xs: '24px', md: '36px', lg: '50px' },
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            border: '1px solid',
            borderColor: theme.palette.grey[600],
          }}
        >
          <Home />
        </Avatar>
      ) : null}
    </Box>
  );
};

interface LocationsListItemProps {
  item: Location;
  index: number;
  isActive: boolean;
  isHover: boolean;
  onListItemClick: (location: Location) => void;
  t?: (key: string, options?: Record<string, unknown>) => string;
}

const LocationsListItem = ({ item, index, isActive, isHover, onListItemClick, t }: LocationsListItemProps) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);

  const listItemMouseClick = useCallback(() => {
    onListItemClick(item);
  }, [item, onListItemClick]);

  const listItemKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        onListItemClick(item);
      }
    },
    [item, onListItemClick]
  );

  return (
    <React.Fragment key={`${item.id}`}>
      <ListItem
        sx={{ ...styles.item, cursor: isActive ? 'unset' : 'pointer' }}
        onClick={listItemMouseClick}
        onKeyDown={listItemKeyDown}
        tabIndex={0}
        title={t('listclick', { label: item.headline })}
      >
        <LocationItem
          statusIcon={<StatusIcon item={item} index={index} isActive={isActive} isHover={isHover} />}
          {...item}
        />
      </ListItem>
      <Divider />
    </React.Fragment>
  );
};

export const LocationsList: React.FunctionComponent<Props> = ({
  locations,
  loading,
  activeId,
  hoverId,
  clearActive,
  onListItemClick,
  t,
}: Props) => {
  const activeItem = locations.find((item) => item.id == activeId);
  const hoverItem = locations.find((item) => item.id == hoverId);

  const theme = useTheme<Theme>();
  const styles = getStyles(theme);

  return (
    <List variant="icon" sx={styles.list}>
      {loading ? (
        <>
          <LocationItemSkeleton key="sk1" />
          <LocationItemSkeleton key="sk2" />
          <LocationItemSkeleton key="sk3" />
        </>
      ) : null}
      {locations.map((item, index) => {
        const isActive =
          item.id == activeId ||
          (item.coordinates &&
            item.coordinates.latitude == activeItem?.coordinates.latitude &&
            item.coordinates.longitude == activeItem?.coordinates.longitude);
        const isHover =
          item.id == hoverId ||
          (item.coordinates &&
            item.coordinates.latitude == hoverItem?.coordinates.latitude &&
            item.coordinates.longitude == hoverItem?.coordinates.longitude);

        if (activeId && !isActive) {
          return null;
        }

        return (
          <LocationsListItem
            key={item.id}
            index={index + 1}
            item={item}
            isActive={isActive}
            isHover={isHover}
            onListItemClick={onListItemClick}
            t={t}
          />
        );
      })}
      {activeId && (
        <Box px={6} paddingTop={9}>
          <Button color="secondary" fullWidth={true} onClick={clearActive}>
            {t('button.showallobjects')}
          </Button>
        </Box>
      )}
    </List>
  );
};
